import { useState, useRef, useEffect, useMemo } from 'react';
import { GetActiveCardFromCustomerByUserIdQuery } from '~/api';
import themeConfig from '../themes.json';
import './loading.css';
import { FrontendPreviewUrl } from '~/config/constant';

export type MinimalStampCardTheme = {
  claimedStampColor: string;
  cardBackground: string;
  textAndBorderColor: string;
  nextStampHighlight: string;
  checkmarkColor: string;
  borderPattern: 'solid' | 'dashed' | 'dotted';
};

interface StampCardIframeProps {
  data?: GetActiveCardFromCustomerByUserIdQuery['getActiveCardFromCustomerByUserId'];
  theme?: Partial<MinimalStampCardTheme>;
  width?: string | number;
  height?: string | number;
  className?: string;
  showPreviewLabel?: boolean;
  onReady?: () => void;
  onHeightChange?: (height: number) => void;
  autoHeight?: boolean;
  scale?: number;
}

const defaultTheme: MinimalStampCardTheme = {
  claimedStampColor: '#007AFF',
  cardBackground: '#2B3E50',
  textAndBorderColor: '#FFFFFF',
  nextStampHighlight: '#FF9500',
  checkmarkColor: '#FFFFFF',
  borderPattern: 'dashed',
};

export const StampCardIframe: React.FC<StampCardIframeProps> = ({
  data,
  theme = themeConfig.presets.classic.theme,
  width = '100%',
  height = 400,
  className = '',
  showPreviewLabel = false,
  onReady,
  onHeightChange,
  autoHeight = true,
  scale = 1,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(typeof height === 'number' ? height : 400);
  const lastReceivedHeight = useRef<number>(0);

  // Merge theme with defaults
  const finalTheme = useMemo(
    () => ({
      ...defaultTheme,
      ...theme,
    }),
    [theme]
  );

  // Transform GraphQL data to iframe format
  const transformedData = useMemo(() => {
    if (!data) return null;

    return {
      id: data.id,
      currentStamps: data.currentStamps || 0,
      totalStampsEarned: data.totalStampsEarned || 0,
      totalStampsRequired: data.totalStampsRequired || 10,
      status: data.status || 'ACTIVE',
      daysRemaining: data.daysRemaining,
      expiresAt: data.expiresAt,
      isNearExpiry: data.isNearExpiry,
      template: data.template
        ? {
            id: data.template.id,
            name: data.template.metadata?.name || 'Stamp Card',
            description: data.template.metadata?.description || '',
            totalStamps: data.totalStampsRequired,
          }
        : undefined,
      stamps:
        data.stamps?.map((stamp) => ({
          index: stamp.index,
          claimed: stamp.claimed,
          containMilestone: stamp.containMilestone,
          remainingStamps: stamp.remainingStamps,
          currentIcon: stamp.currentIcon,
          milestones: stamp.milestones?.map((milestone) => ({
            milestoneStamps: milestone.milestoneStamps,
            isClaimed: milestone.isClaimed,
            rewardDetail: milestone.rewardDetail
              ? {
                  id: milestone.rewardDetail.id,
                  name: milestone.rewardDetail.name,
                }
              : undefined,
          })),
        })) || [],
    };
  }, [data]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      if (event.data.type === 'ready') {
        setIframeReady(true);
        setIsLoading(false);
        onReady?.();

        // Send initial data and theme when iframe is ready
        if (transformedData) {
          sendMessage({
            type: 'updateData',
            data: transformedData,
          });
        }

        sendMessage({
          type: 'updateTheme',
          theme: finalTheme,
        });

        if (showPreviewLabel) {
          sendMessage({
            type: 'updatePreview',
            showPreview: true,
          });
        }
      }

      if (event.data.type === 'heightUpdate' && autoHeight) {
        // Only update if height changed significantly (more than 5px difference)
        const receivedHeight = event.data.height;
        if (Math.abs(receivedHeight - lastReceivedHeight.current) > 5) {
          lastReceivedHeight.current = receivedHeight;
          // Add padding to ensure full content visibility
          const newHeight = Math.min(receivedHeight + 60, 900);
          setIframeHeight(newHeight);
          onHeightChange?.(newHeight);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [autoHeight, transformedData, finalTheme, showPreviewLabel, onReady, onHeightChange]);

  // Update data when it changes
  useEffect(() => {
    if (iframeReady && transformedData) {
      sendMessage({
        type: 'updateData',
        data: transformedData,
      });
    }
  }, [transformedData, iframeReady]);

  // Update theme when it changes
  useEffect(() => {
    if (iframeReady) {
      sendMessage({
        type: 'updateTheme',
        theme: finalTheme,
      });
    }
  }, [finalTheme, iframeReady]);

  // Update preview label when it changes
  useEffect(() => {
    if (iframeReady) {
      sendMessage({
        type: 'updatePreview',
        showPreview: showPreviewLabel,
      });
    }
  }, [showPreviewLabel, iframeReady]);

  const sendMessage = (message: any) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  };

  const iframeUrl = `${FrontendPreviewUrl}/preview/stamp-card?iframe=true`;

  const finalHeight = autoHeight
    ? iframeHeight
    : typeof height === 'number'
    ? height
    : parseInt(height);
  const scaledHeight = finalHeight * scale;
  const scaledWidth = typeof width === 'number' ? width * scale : width;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        width: scaledWidth,
        height: scaledHeight,
        minHeight: scaledHeight,
      }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="loading-overlay" style={{ height: `${scaledHeight}px` }}>
          {/* Loading bar container */}
          <div className="loading-bar-container">
            <div className="loading-bar" />
          </div>
          <p className="loading-text">Loading stamp card...</p>
        </div>
      )}

      {/* Iframe wrapper with scale transform */}
      <div
        style={{
          overflow: 'hidden',
        }}
      >
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className={`${className} ${isLoading ? 'invisible' : 'visible'}`}
          style={{
            width: '100%',
            height: `${finalHeight}px`,
            border: 'none',
            borderRadius: `${10 / scale}px`,
            transition: autoHeight ? 'height 0.3s ease' : undefined,
            display: 'block',
            overflow: 'hidden',
          }}
          title="Stamp Card"
          loading="lazy"
          sandbox="allow-scripts allow-same-origin"
          onLoadStart={() => {
            setIsLoading(true);
          }}
          onLoad={() => {
            // Fallback in case the ready message doesn't come through
            setTimeout(() => {
              if (isLoading) {
                setIsLoading(false);
              }
            }, 3000);
          }}
        />
      </div>
    </div>
  );
};

export default StampCardIframe;
